/**
 * Migração de identidade para dados legados (schema < 1.1.0).
 *
 * Antes: `table.id = fullName` (`schema.name`) e colunas sem `id`.
 * Depois: `table.id`/`col.id` = UUID estável.
 *
 * Opera sobre estruturas CRUS (antes da normalização), injetando ids
 * DETERMINÍSTICOS (`legacyUid`) onde faltam — para que o MESMO commit antigo
 * gere os MESMOS ids nos dois lados de um 3-way merge. Remapeia em conjunto:
 * `rel.from`/`rel.to`, chaves de `positions` e `hiddenTableIds`.
 *
 * Usado em dois pontos: `metadataToEditorState` (metadata do backend) e
 * `onRehydrateStorage` do diagramStore (localStorage `erd_editor_v5`).
 */

import { buildFullTableName } from '@/utils/normalizers'
import { isUuidLike, legacyUid } from '@/utils/uid'

type RawObj = Record<string, unknown>

interface RawMigrationInput {
  tables: unknown[]
  rels: unknown[]
  positions?: Record<string, unknown>
  hiddenTableIds?: unknown[]
}

export interface RawMigrationOutput {
  tables: RawObj[]
  rels: RawObj[]
  positions: Record<string, unknown>
  hiddenTableIds: string[]
  /** true se alguma identidade foi reescrita (algo era legado). */
  changed: boolean
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback
}

/** Converte coluna legada (array OU objeto) em objeto com `id` determinístico. */
function migrateColumn(col: unknown, tableFullName: string): RawObj {
  if (Array.isArray(col)) {
    // LegacyColumnArray: [key, name, type, notNull, blank, desc]
    const name = str(col[1])
    return {
      id: legacyUid('c', tableFullName, name),
      key: col[0],
      name,
      type: col[2],
      notNull: col[3],
      blank: col[4],
      desc: col[5],
    }
  }
  const obj = (col && typeof col === 'object' ? col : {}) as RawObj
  if (str(obj.id)) return obj
  return { ...obj, id: legacyUid('c', tableFullName, str(obj.name)) }
}

export function migrateDiagramIdentity(input: RawMigrationInput): RawMigrationOutput {
  let changed = false
  // oldId/fullName → newId
  const idMap = new Map<string, string>()

  const tables: RawObj[] = (input.tables ?? []).map((raw) => {
    const t = (raw && typeof raw === 'object' ? raw : {}) as RawObj
    const schema = str(t.schema)
    const name = str(t.name)
    const fullName = str(t.fullName) || buildFullTableName(schema, name) || str(t.id)
    const oldId = str(t.id) || fullName

    let newId = oldId
    if (!isUuidLike(oldId)) {
      newId = legacyUid('t', fullName)
      changed = true
    }
    idMap.set(oldId, newId)
    if (fullName) idMap.set(fullName, newId)

    const cols = Array.isArray(t.cols) ? t.cols.map((c) => migrateColumn(c, fullName)) : []
    return { ...t, id: newId, cols }
  })

  const remapId = (v: unknown): string => {
    const s = str(v)
    return idMap.get(s) ?? s
  }

  const rels: RawObj[] = (input.rels ?? []).map((raw) => {
    const r = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as RawObj
    return { ...r, from: remapId(r.from), to: remapId(r.to) }
  })

  const positions: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(input.positions ?? {})) {
    positions[remapId(k)] = v
  }

  const hiddenTableIds: string[] = (input.hiddenTableIds ?? []).map((v) => remapId(v))

  return { tables, rels, positions, hiddenTableIds, changed }
}
